import { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, Hand } from "lucide-react";

// Custom hook for audio calibration
function useCalibrationAudio() {
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const stopCalibrationSound = useCallback(() => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch {
        // Oscillator already stopped - this is expected when component unmounts
      }
      oscillatorRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const playCalibrationSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      
      // Create pink noise for calibration (similar to hand rubbing sound)
      const bufferSize = 2 * audioContext.sampleRate;
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
        output[i] *= 0.11; // Normalize
        b6 = white * 0.115926;
      }
      
      const noiseNode = audioContext.createBufferSource();
      noiseNode.buffer = noiseBuffer;
      noiseNode.loop = true;
      
      gainNodeRef.current = audioContext.createGain();
      gainNodeRef.current.gain.value = 0.3; // Moderate volume
      
      noiseNode.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioContext.destination);
      
      noiseNode.start();
      oscillatorRef.current = noiseNode;
      setIsPlaying(true);
    } catch (error) {
      // Audio context creation failed - likely due to browser restrictions
      setIsPlaying(false);
    }
  }, []);

  const toggleSound = useCallback(() => {
    if (isPlaying) {
      stopCalibrationSound();
    } else {
      playCalibrationSound();
    }
  }, [isPlaying, stopCalibrationSound, playCalibrationSound]);

  const cleanup = useCallback(() => {
    stopCalibrationSound();
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, [stopCalibrationSound]);

  return { isPlaying, toggleSound, stopCalibrationSound, cleanup };
}

// Step 1 Component
function CalibrationStep1({ onNext, onBack }) {
  return (
    <div className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#22D3EE] mb-3">
          Calibration Step 1 of 2
        </p>

        <h2 className="text-2xl sm:text-3xl font-medium text-[#F8FAFC] mb-4 font-['Outfit']">
          Let's calibrate your volume
        </h2>

        <p className="text-[#94A3B8] max-w-md mx-auto mb-8">
          First, <strong className="text-[#F8FAFC]">take your headphones off</strong> and
          rub your hands together closely in front of your nose, quickly and firmly.
        </p>

        <div className="w-32 h-32 bg-[#1E293B] rounded-full flex items-center justify-center mx-auto mb-6 relative">
          <Hand className="w-16 h-16 text-[#22D3EE] hand-rub-animation" />
          <div className="absolute inset-0 rounded-full border-2 border-[#22D3EE]/30 animate-ping" />
        </div>
      </div>

      <div className="bg-[#1E293B] rounded-xl p-4 mb-8">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-[#60A5FA] flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-[#94A3B8] font-medium text-sm">
              Why hand rubbing?
            </p>
            <p className="text-[#64748B] text-sm mt-1">
              Firmly rubbing your hands generates a predictable volume that we'll
              use to ensure you start the screening at the right level.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={onNext}
          className="w-full py-4 px-6 bg-[#22D3EE] text-[#0A0F1C] font-semibold rounded-lg hover:bg-[#67E8F9] transition-all flex items-center justify-center gap-2"
          data-testid="calibration-step1-next-btn"
        >
          I've Done This
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </button>

        <button
          onClick={onBack}
          className="w-full py-3 px-6 rounded-lg border border-[#334155] text-[#94A3B8] font-medium hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors flex items-center justify-center gap-2"
          data-testid="calibration-step1-back-btn"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
      </div>
    </div>
  );
}

// Step 2 Component
function CalibrationStep2({ onNext, onBack }) {
  const { isPlaying, toggleSound, stopCalibrationSound, cleanup } = useCalibrationAudio();

  // Cleanup audio on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleNext = useCallback(() => {
    stopCalibrationSound();
    onNext();
  }, [stopCalibrationSound, onNext]);

  const handleBack = useCallback(() => {
    stopCalibrationSound();
    onBack();
  }, [stopCalibrationSound, onBack]);

  return (
    <div className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#22D3EE] mb-3">
          Calibration Step 2 of 2
        </p>

        <h2 className="text-2xl sm:text-3xl font-medium text-[#F8FAFC] mb-4 font-['Outfit']">
          Match the volume
        </h2>

        <p className="text-[#94A3B8] max-w-md mx-auto">
          Now <strong className="text-[#F8FAFC]">put your headphones back on</strong> and
          adjust your device volume to match the sound of your hands rubbing together.
        </p>
      </div>

      <div className="bg-[#1E293B] rounded-xl p-6 mb-8">
        <p className="text-[#64748B] text-sm text-center mb-4">Calibration Track</p>
        
        <button
          onClick={toggleSound}
          className={`w-full py-4 rounded-xl flex items-center justify-center gap-3 transition-all ${
            isPlaying
              ? "bg-[#22D3EE] text-[#0A0F1C]"
              : "bg-[#111827] border border-[#334155] text-[#F8FAFC] hover:border-[#22D3EE]"
          }`}
          data-testid="calibration-play-btn"
        >
          {isPlaying ? (
            <>
              <div className="sound-wave">
                <div className="bar" />
                <div className="bar" />
                <div className="bar" />
                <div className="bar" />
                <div className="bar" />
              </div>
              <span className="font-semibold">Playing...</span>
            </>
          ) : (
            <>
              <Volume2 className="w-6 h-6" />
              <span className="font-semibold">Play Calibration Sound</span>
            </>
          )}
        </button>

        <p className="text-[#64748B] text-xs text-center mt-4">
          Adjust your device volume until the sound matches your hand rubbing
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={handleNext}
          className="w-full py-4 px-6 bg-[#22D3EE] text-[#0A0F1C] font-semibold rounded-lg hover:bg-[#67E8F9] transition-all flex items-center justify-center gap-2"
          data-testid="calibration-step2-next-btn"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          Volume is Calibrated
        </button>

        <button
          onClick={handleBack}
          className="w-full py-3 px-6 rounded-lg border border-[#334155] text-[#94A3B8] font-medium hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors flex items-center justify-center gap-2"
          data-testid="calibration-step2-back-btn"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
      </div>
    </div>
  );
}

export default function VolumeCalibration({ step, onNext, onBack }) {
  if (step === 1) {
    return <CalibrationStep1 onNext={onNext} onBack={onBack} />;
  }
  
  return <CalibrationStep2 onNext={onNext} onBack={onBack} />;
}
